import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import cx from 'classnames';

import { Icon as OrcIcon } from '@ontruck/orc';

import Icon from '@ontruck/ontruck-react-components/lib/Icon';
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@ontruck/ontruck-react-components/lib/Modal';
import Input from '@ontruck/ontruck-react-components/lib/Input';
import Button from '@ontruck/ontruck-react-components/lib/Button';
import Checkbox from '@ontruck/ontruck-react-components/lib/Checkbox';

import ButtonGroup from '@ontruck/ontruck-react-components/lib/ButtonGroup';
import { OptionGroup, Option } from '@ontruck/ontruck-react-components/lib/Option';

import { vehicleTemperatureMap } from 'utils/temperature';

import style from './style.module.scss';

import { Section, SectionTitle, Row, Column } from 'components/ModalFormLayout';

import { allowOnlyNumbersAndDot } from 'utils/forms';

import CheckAndDateList from 'components/CheckAndDateList';
import DateField from 'components/DateField';

const classTemperatureEnabled = ({ type = '?', isolation = '?', _class = '?' }) => {
  if (!vehicleTemperatureMap[type] || !vehicleTemperatureMap[type][isolation]) {
    return false;
  }

  return Object.keys(vehicleTemperatureMap[type][isolation]).indexOf(_class) !== -1;
};

const VehicleDetail = ({
  show = true,
  handleCloseModal,
  availableVehiclesTypes,
  intl,
  stateVehicle,
  submitting,
  serverErrors,
  handleShowFieldError,
  handleSave,
  handleTemperatureChange,
  handleFieldChange,
  dateList,
  vehicleTypesToIconsName,
}) => {
  const { carrier } = stateVehicle;

  if (!carrier) return null;

  const t = intl.messages.components.VehicleEditModal;
  const currency = intl.messages.currencies[carrier.currency];

  return (
    <Modal show={show} size="L" onClose={handleCloseModal}>
      <ModalHeader>{stateVehicle.id ? t.titles.modal_edit : t.titles.modal_new}</ModalHeader>
      <ModalBody noPadding light>
        <Section>
          <Row>
            <Column>
              {handleShowFieldError('vehicle_type') ? (
                <div className={style.vehicleError}>
                  <Icon name="alert" className={style.vehicleErrorIcon} /> {t.errors.vehicle_type}
                </div>
              ) : null}
              <ButtonGroup
                active={stateVehicle.vehicle_type}
                className={cx(style.vehicleTypeContainer, {
                  [style.error]: handleShowFieldError('vehicle_type'),
                })}
                buttonClassName={style.vehicleType}
                buttonSelectedClassName={style.vehicleTypeSelected}
                onChange={handleFieldChange('vehicle_type', [], () => {
                  if (stateVehicle.vehicle_type === 'tractor_unit') {
                    handleTemperatureChange('type')('DRY');
                  }
                })}
              >
                {(availableVehiclesTypes[carrier.country] || []).map((type) => (
                  <Button value={type} key={type}>
                    <div>
                      <OrcIcon glyph={vehicleTypesToIconsName[type]} size="6rem" color="greyWolf" />
                      <p>{t.labels.vehicle_types[type]}</p>
                    </div>
                  </Button>
                ))}
              </ButtonGroup>
            </Column>
          </Row>

          <Row>
            <Column>
              <SectionTitle>{t.titles.specs}</SectionTitle>
              <div className={style.linealInputs}>
                <div>
                  <Input
                    label={t.labels.plate_number}
                    className={style.inputSizeM}
                    value={
                      (stateVehicle.plate_number && stateVehicle.plate_number.toUpperCase()) || ''
                    }
                    onChange={handleFieldChange('plate_number')}
                    error={(handleShowFieldError('plate_number') && t.errors.plate_number) || null}
                  />
                </div>
                <div>
                  <Input
                    label={t.labels['length']}
                    helpText={t.labels.length_help}
                    className={style.inputSizeS}
                    value={stateVehicle['length'] || ''}
                    onChange={handleFieldChange('length')}
                    {...allowOnlyNumbersAndDot}
                  />
                </div>
                <div>
                  <Input
                    label={t.labels.mma}
                    helpText={t.labels.mma_help}
                    className={style.inputSizeS}
                    value={parseInt(stateVehicle.mma, 10) || ''}
                    onChange={handleFieldChange('mma')}
                    {...allowOnlyNumbersAndDot}
                  />
                </div>
              </div>
            </Column>
          </Row>
          <Row>
            <Column third>
              <div className={style.linealInputs}>
                <div>
                  <Input
                    label={t.labels.make}
                    value={stateVehicle.make || ''}
                    className={style.inputSizeM}
                    onChange={handleFieldChange('make')}
                  />
                </div>
                <div>
                  <Input
                    label={t.labels.model}
                    value={stateVehicle.model || ''}
                    className={style.inputSizeM}
                    onChange={handleFieldChange('model')}
                  />
                </div>
              </div>
            </Column>
          </Row>
        </Section>

        {stateVehicle.vehicle_type !== 'tractor_unit' ? (
          <Section>
            <SectionTitle>{t.titles.load_capacity}</SectionTitle>
            <Row>
              <Column className={style.linealInputs}>
                <div>
                  <Input
                    label={t.labels.max_weight}
                    helpText={t.labels.max_weight_help}
                    className={style.inputSizeS}
                    value={parseInt(stateVehicle.max_weight, 10) || ''}
                    onChange={handleFieldChange('max_weight')}
                    {...allowOnlyNumbersAndDot}
                  />
                </div>
                <div>
                  <Input
                    label={t.labels.max_pallets}
                    className={style.inputSizeS}
                    value={stateVehicle.max_pallets || ''}
                    onChange={handleFieldChange('max_pallets')}
                    {...allowOnlyNumbersAndDot}
                  />
                </div>
                <div className={style.noMargin}>
                  <Input
                    label={t.labels.container_width}
                    helpText={t.labels.container_width_help}
                    className={style.inputSizeS}
                    value={stateVehicle.container_width || ''}
                    onChange={handleFieldChange('container_width')}
                    {...allowOnlyNumbersAndDot}
                  />
                </div>
                <div>
                  <Input
                    label={t.labels.container_height}
                    helpText={t.labels.container_height_help}
                    className={style.inputSizeS}
                    value={stateVehicle.container_height || ''}
                    onChange={handleFieldChange('container_height')}
                    {...allowOnlyNumbersAndDot}
                  />
                </div>
                <div>
                  <Input
                    label={t.labels.container_length}
                    helpText={t.labels.container_length_help}
                    className={style.inputSizeS}
                    value={stateVehicle.container_length || ''}
                    onChange={handleFieldChange('container_length')}
                    {...allowOnlyNumbersAndDot}
                  />
                </div>
              </Column>
            </Row>
            <Row>
              <Column third>
                <Checkbox
                  label={t.labels.has_liftgate}
                  boxed
                  checked={stateVehicle.has_liftgate}
                  onChange={handleFieldChange('has_liftgate')}
                />
              </Column>
              <Column third>
                <Checkbox
                  label={t.labels.has_lateral_loading}
                  boxed
                  checked={stateVehicle.has_lateral_loading}
                  onChange={handleFieldChange('has_lateral_loading')}
                />
              </Column>
              <Column third>
                <Checkbox
                  label={t.labels.has_superior_loading}
                  boxed
                  checked={stateVehicle.has_superior_loading}
                  onChange={handleFieldChange('has_superior_loading')}
                />
              </Column>
            </Row>
          </Section>
        ) : null}

        {stateVehicle.vehicle_type !== 'tractor_unit' ? (
          <Section>
            {handleShowFieldError('temperature_details') ? (
              <div className={style.vehicleError}>
                <Icon name="alert" className={style.vehicleErrorIcon} />{' '}
                {t.errors.temperature_details}
              </div>
            ) : (
              <SectionTitle>{t.titles.temperature}</SectionTitle>
            )}
            <Row>
              <Column>
                <ButtonGroup
                  active={stateVehicle.temperature_details.type}
                  lightColored
                  onChange={handleTemperatureChange('type', {
                    clearErrors: ['temperature_details', 'has_thermograph'],
                    resetFields: ['_class', 'isolation'],
                    resetCommonFields: [
                      'has_thermograph',
                      'temperature_chambers',
                      'temperature_independent_engine',
                    ],
                  })}
                  className={cx(style.temperatureContainer, {
                    [style.error]: handleShowFieldError('temperature_details'),
                  })}
                >
                  <Button value={'DRY'}>{t.labels.temperature_details_type['DRY']}</Button>
                  <Button value="F">{t.labels.temperature_details_type['F']}</Button>
                  <Button value="R">{t.labels.temperature_details_type['R']}</Button>
                  <Button value="I">{t.labels.temperature_details_type['I']}</Button>
                  <Button value="C">{t.labels.temperature_details_type['C']}</Button>
                </ButtonGroup>
              </Column>
            </Row>
            {stateVehicle.temperature_details.type &&
            stateVehicle.temperature_details.type !== 'DRY' ? (
              <Row>
                <Column half>
                  <SectionTitle>{t.titles.isolation}</SectionTitle>
                  <ButtonGroup
                    active={stateVehicle.temperature_details.isolation}
                    lightColored
                    onChange={handleTemperatureChange('isolation', {
                      resetFields: ['_class'],
                    })}
                  >
                    <Button value="R">{t.labels.isolation['R']}</Button>
                    <Button value="N">{t.labels.isolation['N']}</Button>
                  </ButtonGroup>
                </Column>
                <Column>
                  {stateVehicle.temperature_details.type !== 'I' ? (
                    <SectionTitle>{t.titles._class}</SectionTitle>
                  ) : null}
                  {stateVehicle.temperature_details.type !== 'I' ? (
                    <ButtonGroup
                      active={stateVehicle.temperature_details._class}
                      lightColored
                      onChange={handleTemperatureChange('_class')}
                    >
                      {['A', 'B', 'C', 'D', 'E', 'F'].map((_class) => (
                        <Button
                          key={_class}
                          value={_class}
                          disabled={
                            !classTemperatureEnabled({
                              ...stateVehicle.temperature_details,
                              _class,
                            })
                          }
                        >
                          {_class}
                        </Button>
                      ))}
                    </ButtonGroup>
                  ) : (
                    <div>&nbsp;</div>
                  )}
                </Column>
              </Row>
            ) : null}

            {stateVehicle.temperature_details.type &&
            stateVehicle.temperature_details.type !== 'DRY' ? (
              <Row>
                <Column equal>
                  <SectionTitle>{t.titles.temperature_chambers}</SectionTitle>
                  <ButtonGroup
                    active={stateVehicle.temperature_chambers}
                    lightColored
                    onChange={handleFieldChange('temperature_chambers')}
                    className={style.noFullWidth}
                  >
                    <Button value={1}>{t.labels.temperature_chambers['1']}</Button>
                    <Button value={2}>{t.labels.temperature_chambers['2']}</Button>
                    <Button value={3}>{t.labels.temperature_chambers['3']}</Button>
                  </ButtonGroup>
                </Column>
                {stateVehicle.temperature_details.type === 'F' &&
                stateVehicle.temperature_details.isolation === 'R' ? (
                  <Column equal>
                    <SectionTitle>{t.titles.temperature_independent_engine}</SectionTitle>
                    <ButtonGroup
                      active={stateVehicle.temperature_independent_engine}
                      lightColored
                      onChange={handleFieldChange('temperature_independent_engine')}
                    >
                      <Button value={false}>
                        <FormattedMessage
                          id={'temperature_independent_engine'}
                          defaultMessage={t.labels.temperature_independent_engine['X']}
                          values={{
                            _class: stateVehicle.temperature_details._class,
                          }}
                        />
                      </Button>
                      <Button value={true}>
                        {t.labels.temperature_independent_engine['AUTO']}
                      </Button>
                    </ButtonGroup>
                  </Column>
                ) : null}
              </Row>
            ) : null}

            {stateVehicle.temperature_details.type &&
            stateVehicle.temperature_details.type !== 'DRY' ? (
              <Row>
                <Column>
                  {handleShowFieldError('has_thermograph') ? (
                    <div className={style.vehicleError}>
                      <Icon name="alert" className={style.vehicleErrorIcon} />{' '}
                      {t.titles.has_thermograph}
                    </div>
                  ) : (
                    <SectionTitle>{t.titles.has_thermograph}</SectionTitle>
                  )}
                  <OptionGroup
                    selected={stateVehicle.has_thermograph}
                    onChange={handleFieldChange('has_thermograph')}
                    error={handleShowFieldError('has_thermograph')}
                  >
                    <Option value={true}>{t.labels.has_thermograph_yes}</Option>
                    <Option value={false}>{t.labels.has_thermograph_no}</Option>
                  </OptionGroup>
                </Column>
              </Row>
            ) : null}
          </Section>
        ) : null}

        <Section>
          <Row>
            <Column half>
              <SectionTitle>{t.titles.docs}</SectionTitle>

              {carrier && carrier.google_drive_link ? (
                <Button
                  href={carrier.google_drive_link}
                  link
                  className={style.docLink}
                  target="_blank"
                >
                  {t.labels.google_drive_link}
                </Button>
              ) : null}

              <Checkbox
                label={t.labels.vehicle_data_checked}
                checked={stateVehicle.vehicle_data_checked}
                onChange={handleFieldChange('vehicle_data_checked')}
                className={style.docCheckbox}
              />
              <Checkbox
                label={t.labels.photos_checked}
                checked={stateVehicle.photos_checked}
                onChange={handleFieldChange('photos_checked')}
                className={style.docCheckbox}
              />

              <Checkbox
                className={style.docCheckbox}
                label={t.labels.has_insurance}
                checked={stateVehicle.has_insurance}
                onChange={(checked) => handleFieldChange('has_insurance')(checked)}
              />
              <div className={cx(style.linealInputs, style.fieldSpacing)}>
                <div>
                  <Input
                    label={t.labels.insurance_value}
                    helpText={currency}
                    className={style.inputSizeS}
                    value={stateVehicle.insurance_value || ''}
                    onChange={handleFieldChange('insurance_value')}
                    disabled={!stateVehicle.has_insurance}
                  />
                </div>
                <div>
                  <Input
                    label={t.labels.insurance_provider}
                    className={style.inputSizeM}
                    value={stateVehicle.insurance_provider || ''}
                    onChange={handleFieldChange('insurance_provider')}
                    disabled={!stateVehicle.has_insurance}
                  />
                </div>
              </div>
              <DateField
                label={t.labels.insurance_expiration}
                value={stateVehicle.insurance_expiration}
                onChange={handleFieldChange('insurance_expiration')}
                disabled={!stateVehicle.has_insurance}
                error={
                  (stateVehicle.insurance_expiration &&
                    handleShowFieldError('insurance_expiration') &&
                    t.errors.date) ||
                  null
                }
              />

              <CheckAndDateList
                fieldList={dateList}
                object={stateVehicle}
                t={t.labels}
                handleFieldChange={handleFieldChange}
                checkboxClassname={style.docCheckbox}
                errors={Object.assign(
                  ...dateList.map((name) =>
                    handleShowFieldError(name) ? { [name]: t.errors.date } : {}
                  )
                )}
              />
            </Column>
            <Column third>
              <SectionTitle>{t.titles.active}</SectionTitle>
              <OptionGroup
                selected={stateVehicle.is_active}
                onChange={handleFieldChange('is_active')}
              >
                <Option value={true}>{t.labels.active_true}</Option>
                <Option value={false}>{t.labels.active_false}</Option>
              </OptionGroup>
            </Column>
          </Row>
        </Section>
      </ModalBody>
      <ModalFooter light>
        {Object.keys(serverErrors).length ? (
          <div className={style.generalErrors}>
            <div>
              {Object.keys(serverErrors).map((error) => (
                <div key={error}>
                  <strong>{error}</strong>: {serverErrors[error]}
                </div>
              ))}
            </div>
          </div>
        ) : null}
        <Button fullWidth onClick={handleSave} disabled={submitting}>
          {stateVehicle.id ? t.save_edit : t.save_new}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

VehicleDetail.propTypes = {
  show: PropTypes.bool,
  handleCloseModal: PropTypes.func,
  availableVehiclesTypes: PropTypes.array,
  intl: PropTypes.any,
  stateVehicle: PropTypes.object,
  submitting: PropTypes.bool,
  serverErrors: PropTypes.object,
  handleShowFieldError: PropTypes.func,
  handleSave: PropTypes.func,
  handleTemperatureChange: PropTypes.func,
  handleFieldChange: PropTypes.func,
  dateList: PropTypes.array,
  vehicleTypesToIconsName: PropTypes.string,
};

export default VehicleDetail;
